<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { DynamicScroller, DynamicScrollerItem } from "vue-virtual-scroller";

import actions from "~/actions";
import EmptyState from "~/components/dumb/EmptyState.vue";
import LoadingSpinner from "~/components/dumb/LoadingSpinner.vue";
import { COMPLETED_STATUS_KINDS, TASK_CONTEXT_MENU_OPTIONS } from "~/components/visualization/constants";
import { MyTasksIcon } from "~/icons";
import { DartboardKind, EditorMode, FilterConnector } from "~/shared/enums";
import type { FilterGroup, Task } from "~/shared/types";
import { useAppStore, useDataStore, usePageStore, useTenantStore, useUserStore } from "~/stores";
import useLastScrollPosition from "~/utils/useLastScrollPosition";
import usePaginatedList from "~/utils/usePaginatedList";

import HomeTaskRow from "./HomeTaskRow.vue";

const VIEW_DUID = "@my-tasks";

const appStore = useAppStore();
const dataStore = useDataStore();
const pageStore = usePageStore();
const tenantStore = useTenantStore();
const userStore = useUserStore();

const completedStatusDuids = computed(
  () =>
    new Set(
      dataStore.getStatusesByKinds([...COMPLETED_STATUS_KINDS], dataStore.defaultStatusProperty).map((e) => e.duid)
    )
);
const ignoredDartboardDuids = computed(
  () =>
    new Set(
      [
        ...dataStore.getDartboardsByKindOrdered(DartboardKind.NEXT),
        ...dataStore.getDartboardsByKindOrdered(DartboardKind.BACKLOG),
      ].map((e) => e.duid)
    )
);
const myTaskValidator = (task: Task) =>
  !task.inTrash &&
  !task.drafterDuid &&
  task.assigneeDuids.includes(userStore.duid) &&
  !completedStatusDuids.value.has(task.statusDuid) &&
  !ignoredDartboardDuids.value.has(task.dartboardDuid);

const tasks = computed(() => dataStore.getLoadedTasksForPageDuid(VIEW_DUID));
const filterGroup = computed<FilterGroup>(() => ({
  filters: appStore.myTasksFilters,
  connector: FilterConnector.AND,
}));

const scroller = ref<InstanceType<typeof DynamicScroller> | null>(null);
const scrollContainer = computed(() => scroller.value?.$el);
const { isLoading, reset } = usePaginatedList(
  computed(() => VIEW_DUID),
  scrollContainer,
  computed(() => dataStore.getLoadedTaskCountForPageDuid(VIEW_DUID)),
  async () => dataStore.loadPaginatedTaskForPageDuid(VIEW_DUID, filterGroup.value, [], myTaskValidator),
  // TODO incremental to improve offset system
  -400
);
useLastScrollPosition(
  computed(() => VIEW_DUID),
  scrollContainer
);

watch([() => appStore.myTasksFilters, completedStatusDuids, ignoredDartboardDuids], () => {
  dataStore.resetPaginationStateForPageDuid(VIEW_DUID);
  reset();
});

const onContextMenu = (event: MouseEvent, targetTask: Task) => {
  if (tenantStore.isDart && !pageStore.adminHidden && event.altKey) {
    return;
  }

  event.preventDefault();
  appStore.openContextMenu(
    event as PointerEvent,
    actions.context.task(targetTask, EditorMode.CONTEXT_MENU, [targetTask], false),
    TASK_CONTEXT_MENU_OPTIONS
  );
};
</script>

<template>
  <div class="relative flex h-80 w-full flex-col gap-4 rounded-xl py-4 pl-4 pr-px bg-lt">
    <DynamicScroller ref="scroller" :items="tasks" :min-item-size="52" key-field="duid" class="h-full flex-1">
      <template #default="{ item: task, active }">
        <DynamicScrollerItem :item="task" :active="active" :size-dependencies="[task.title]" class="select-none pb-2">
          <HomeTaskRow :duid="task.duid" @context-menu="(event: MouseEvent) => onContextMenu(event, task)" />
        </DynamicScrollerItem>
      </template>
      <template v-if="tasks.length !== 0" #after>
        <LoadingSpinner v-if="isLoading" class="mt-2" />
        <div class="min-h-12 w-full" />
      </template>
    </DynamicScroller>
    <template v-if="tasks.length === 0">
      <LoadingSpinner v-if="isLoading" absolute />
      <EmptyState v-else :main-icon="MyTasksIcon" title="There are no tasks assigned to you" inline />
    </template>
  </div>
</template>
